<template>
  <v-card data-testid="tool_hart_chart">
    <v-card-title style="padding: 0px">
      <v-toolbar grey flat>
        <v-btn icon @click="close" data-testid="tool_hart_chart_close">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t("common.hart_chart") }}</v-toolbar-title>
      </v-toolbar>
    </v-card-title>
    <v-card-text>
      <v-layout align-center justify-space-around row>
        <v-flex xs5>
          <v-select
            v-model="pages"
            :items="pages_options"
            :label="$t('tool_hart_chart.pages')"
          />
        </v-flex>

        <v-flex xs5>
          <v-select
            v-model="chars"
            :items="chars_options"
            :label="$t('tool_hart_chart.characters')"
          />
        </v-flex>
      </v-layout>

      <v-layout align-center justify-space-around row>
        <v-flex xs5>
          <v-text-field
            v-model="rows"
            :label="$t('tool_hart_chart.rows')"
            type="number"
            min="4"
            max="15"
          />
        </v-flex>

        <v-flex xs5>
          <v-text-field
            v-model="cols"
            :label="$t('tool_hart_chart.columns')"
            type="number"
            min="4"
            max="15"
          />
        </v-flex>
      </v-layout>

      <v-layout align-center justify-space-around row>
        <v-flex xs5>
          <v-text-field
            v-model="large_font_pt"
            :label="$t('tool_hart_chart.far_font_size')"
            type="number"
            min="6"
            max="60"
            suffix="pt"
          />
        </v-flex>

        <v-flex xs5>
          <v-text-field
            v-model="small_font_pt"
            :label="$t('tool_hart_chart.near_font_size')"
            type="number"
            min="6"
            max="60"
            suffix="pt"
          />
        </v-flex>

      </v-layout>

      <v-layout align-center justify-space-around row>
        <v-flex xs5>
          <v-select v-if="chars == 'both' || chars == 'letters' || chars == 'lowerboth'" v-model="alphabet" :label="$t('common.alphabet')" :items="alphabet_options" />

        </v-flex>

        <v-flex xs5>
          <v-select
            v-model="font"
            :items="fonts_options"
            :label="$t('common.font')"
          />
        </v-flex>
      </v-layout>

      <v-layout align-center justify-space-around row>
        <v-flex xs5>
          <v-select v-model="align" :label="$t('common.alignment')" :items="align_options" />
        </v-flex>

        <v-flex xs5/>
      </v-layout>


    </v-card-text>

    <v-card-actions>
      <v-btn @click="view()" data-testid="tool_hart_chart_view">{{ $t("common.view") }}</v-btn>
      <v-spacer />
      <v-btn color="primary" @click="print()" data-testid="tool_hart_chart_print">{{ $t("common.print") }}</v-btn>
    </v-card-actions>
    <v-progress-linear v-if="in_flight" :indeterminate="true" />
  </v-card>
</template>

<script>
import printJS from "print-js";
import languages from "../../../../data/lang_codes.json";
import { Chance } from "chance";
let chance = Chance();

function alphabetHasCase(alphabet) {
  return Object.values(languages).some(
    (lang) => (lang.alphabet || "latin") === alphabet && !lang.single_case
  );
}

export default {
  components: {},
  props: {},
  data() {

    let alphabet = "latin";
    if (languages[this.$i18n.lang()] && languages[this.$i18n.lang()].alphabet) {
      alphabet = languages[this.$i18n.lang()].alphabet;
    }

    let data = {
      rows: 10,
      cols: 10,
      large_font_pt: 32,
      small_font_pt: 10,
      pages: "both",
      font: "OpticalSans",
      alphabet: alphabet,
      align: "center",
      pages_options: [
        { text: this.$t("tool_hart_chart.near_and_far"), value: "both" },
        { text: this.$t("tool_hart_chart.far_page_only"), value: "far" },
        { text: this.$t("tool_hart_chart.near_page_only"), value: "near" },
      ],
      chars: "both",
      alphabet_options: [
        { text: this.$t("common.alphabet_latin"), value: "latin" },
        { text: this.$t("common.alphabet_arabic"), value: "arabic" },
        { text: this.$t("common.alphabet_greek"), value: "greek" },
        { text: this.$t("common.alphabet_hebrew"), value: "hebrew" }
      ],
      align_options: [
        { text: this.$t("common.center"), value: "center" },
        { text: this.$t("common.top"), value: "top" },
      ],
      in_flight: false,
    };
    return data;
  },
  computed: {
    chars_options() {
      const hasCase = alphabetHasCase(this.alphabet);
      
      let options = [
        { text: this.$t("tool_hart_chart.numbers_and_letters"), value: "both" },
        { text: this.$t("tool_hart_chart.letters_only"), value: "letters" },
      ];
      
      // Only show "Numbers and lowercase letters" for alphabets that support lowercase
      if (hasCase) {
        options.push({ text: this.$t("tool_hart_chart.numbers_and_lowercase_letters"), value: "lowerboth" });
      }
      
      options.push(
        { text: this.$t("tool_hart_chart.numbers_only"), value: "numbers" },
        { text: this.$t("tool_hart_chart.s_5"), value: "sfive" }
      );
      
      return options;
    },
    fonts_options() {
      if (this.alphabet == "latin" || this.chars == "numbers" || this.chars == "sfive") {
        return [
          { text: "Optical Sans", value: "OpticalSans" },
          { text: "Arial", value: "Arial" },
          { text: "Courier", value: "Courier" },
          { text: "Helvetica", value: "Helvetica" },
          { text: "Times New Roman", value: "Times New Roman" },
          { text: "Georgia", value: "Georgia" },
          { text: "Verdana", value: "Verdana" },
          { text: "Comic Sans", value: "Comic Sans MS"}
        ];
      } else {
        return [
          { text: "Optical Sans", value: "OpticalSans" },
        ];
      }
    }
  },
  watch: {
    chars() {
      // Optical Sans doesn't support lowercase characters
      if (this.chars == "lowerboth" && this.font == "OpticalSans") {
        this.font = "Helvetica";
      }
    },
    alphabet() {
      if (this.alphabet != "latin") {
        this.font = "OpticalSans";
      }
      // If current chars option is not available for new alphabet, reset to a valid option
      const hasCase = alphabetHasCase(this.alphabet);
      if (this.chars == "lowerboth" && !hasCase) {
        this.chars = "both";
      }
    }
  },
  methods: {
    view: function () {
      window.open(this.url(), "_blank");
    },
    url: function () {
      let chars = this.chars;
      if (this.chars == "lowerboth") {
        // Handle "Numbers and lowercase letters" option
        if (this.alphabet == "greek") {
          chars = "lowergreekboth";
        } else {
          chars = "lowerboth";
        }
      } else if (this.alphabet != "latin" && (this.chars == "letters" || this.chars == "both")) {
        chars = this.alphabet + this.chars;
      }

      let uri = "/api/tools/pdf/hart_chart.pdf";
      uri += "?rows=" + this.rows;
      uri += "&cols=" + this.cols;
      uri += "&large_font_pt=" + this.large_font_pt;
      uri += "&small_font_pt=" + this.small_font_pt;
      uri += "&pages=" + this.pages;
      uri += "&chars=" + chars;
      uri += "&font=" + encodeURIComponent(this.font);
      uri += "&align=" + this.align;
      uri += "&cache_buster=" + chance.hex();

      return uri;
    },
    print: function () {
      let self = this;

      if (navigator.vendor.includes("Apple")) {
        window.open(this.url(), "_blank");
        return;
      }

      printJS({
        printable: self.url(),
        onLoadingStart: function () {
          self.in_flight = true;
        },
        onLoadingEnd: function () {
          self.in_flight = false;
        },
      });
    },
    close: function () {
      this.$emit("close");
    },
  },
};
</script>

<style></style>
